import { useQueryClient } from '@tanstack/react-query';
import { platformApi } from '../../../../api/endpoints/platform';
import { RoleFormCore } from '../../../../components/roles/RoleFormCore';
import type { TenantRoleListItemLocal } from '../../../../api/types/TenantLocal';
import type { Permission } from '../../../../api/types/permissions';

interface Props {
    /** tenantId del DRILL-DOWN (param de ruta). El rol vive en ESTE tenant, no en el del actor. */
    tenantId: string;
    /** Si viene, el form edita ese rol (roleId fijo); si no, crea uno nuevo. */
    role?: TenantRoleListItemLocal;
    /** Se llama tras crear/editar OK (ej. cerrar el modal del padre). */
    onDone?: () => void;
    /** Precarga el nombre al crear (ej. «Clonar a personalizado» → «Copia de X»). */
    initialName?: string;
    /** Precarga los permisos al crear (ej. clonar un rol de sistema). */
    initialPermissions?: Permission[];
}

/** Alta y edición de un rol personalizado del tenant visto desde plataforma, con el catálogo de ese tenant. */
export function PlatformTenantRoleForm({
    tenantId,
    role,
    onDone,
    initialName,
    initialPermissions,
}: Props) {
    const queryClient = useQueryClient();

    return (
        <RoleFormCore
            strategy={{
                supportsEdit: true,
                role: role ? { roleId: role.roleId } : undefined,
                // El nombre editable arranca del `roleName` del rol (comportamiento preservado del flujo).
                initial: {
                    displayName: role?.roleName ?? initialName ?? '',
                    description: role?.description ?? '',
                    permissions: role?.permissions ?? initialPermissions ?? [],
                },
                create: (body, opts) => platformApi.createTenantRole(tenantId, body, opts),
                update: role
                    ? (body) => platformApi.updateTenantRole(tenantId, role.roleId, body)
                    : undefined,
                invalidate: () =>
                    queryClient.invalidateQueries({
                        queryKey: ['platform', 'tenants', tenantId, 'roles'],
                    }),
                permissionPickerProps: { tenantId },
                onDone,
                idPrefix: 'ptrf',
                nameHint: 'Cómo quieres que se vea. Ej: Supervisor de Cobranza',
                permissionsLabel: 'Permisos',
                createSubmitLabel: 'Crear rol',
            }}
        />
    );
}
